'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { createGame, tick, typeChar, nearestWordProgress } from '@/features/games/word-defense/logic';
import { recordGameAttempt, getGameRecord } from '@/lib/storage/games';
import { appendAttempt } from '@/lib/storage/attempts';
import { useGameLoop } from '@/hooks/use-game-loop';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export function WordDefenseGame({ defaultDifficulty = 'medium' }) {
  const [difficulty, setDifficulty] = useState(defaultDifficulty);
  const [seed, setSeed] = useState(1);
  const [state, setState] = useState(() => createGame({ seed: 1, difficulty: defaultDifficulty }));
  const [now, setNow] = useState(0);
  const [record, setRecord] = useState(null);
  const inputRef = useRef(null);

  useGameLoop({
    phase: state.phase,
    tick: (nowMs) => {
      setNow(nowMs);
      setState((s) => (s.phase === 'running' ? tick(s, nowMs) : s));
    },
  });

  useEffect(() => { setRecord(getGameRecord(`word-defense:${difficulty}`)); }, [difficulty]);

  useEffect(() => {
    if (state.phase === 'ended' && state.startedAtMs > 0) {
      const elapsedSec = Math.max(1, (now - state.startedAtMs) / 1000);
      const wpm = (state.correctTyped * 5 * 60) / elapsedSec;
      setRecord(recordGameAttempt({ gameId: `word-defense:${difficulty}`, score: state.score, wpm }));
      appendAttempt({
        activityType: 'game',
        activityRefSlug: `word-defense:${difficulty}`,
        durationSeconds: Math.round(elapsedSec),
        grossWpm: 0,
        netWpm: Math.round(wpm),
        accuracy: state.correctTyped + state.incorrectTyped > 0
          ? (state.correctTyped / (state.correctTyped + state.incorrectTyped)) * 100
          : 100,
        correctChars: state.correctTyped * 4,
        incorrectChars: state.incorrectTyped,
        totalChars: state.correctTyped * 4 + state.incorrectTyped,
        backspaces: 0,
      });
    }
  }, [state.phase, state.score, state.correctTyped, state.incorrectTyped, state.startedAtMs, difficulty, now]);

  useEffect(() => { if (state.phase === 'running') inputRef.current?.focus(); }, [state.phase]);

  const reset = useCallback((d = difficulty) => {
    setSeed((s) => s + 1);
    setState(createGame({ seed: seed + 1, difficulty: d }));
    setNow(0);
  }, [seed, difficulty]);

  const onKeyDown = (e) => {
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
    e.preventDefault();
    setState((s) => typeChar(s, e.key));
  };

  const threat = nearestWordProgress(state, now || state.startedAtMs || 1);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1">
          {['easy', 'medium', 'hard'].map((d) => (
            <Button key={d} size="sm" variant={difficulty === d ? 'primary' : 'secondary'}
              onClick={() => { setDifficulty(d); reset(d); }}
              disabled={state.phase === 'running'}
            >{d}</Button>
          ))}
        </div>
        <div className="flex items-center gap-4 text-sm tabular-nums">
          <span className="text-text-secondary">Score <strong className="text-text-primary">{state.score}</strong></span>
          <span className="text-text-secondary">Threat <strong className="text-text-primary">{Math.round(threat * 100)}%</strong></span>
        </div>
      </div>

      <div className="relative h-64 overflow-hidden rounded-lg border border-border bg-surface-sunken">
        <div className="absolute right-0 top-1/2 -translate-y-1/2 rounded-l-md bg-error px-2 py-6 text-xs font-semibold text-white">
          TARGET
        </div>
        {state.words.map((w) => {
          const p = Math.min(1, (now - w.spawnedAtMs) / w.travelMs);
          const rightOffset = p * 90;
          return (
            <div key={w.id}
              className="absolute rounded bg-brand-100 px-2 py-1 font-mono text-sm text-brand-900 shadow-sm dark:bg-brand-900 dark:text-brand-100"
              style={{ top: `${(w.id.charCodeAt(2) * 13) % 80}%`, right: `${rightOffset}%` }}>
              {w.text}
            </div>
          );
        })}
      </div>

      <input ref={inputRef} className="sr-only" aria-label="Word Defense input" value="" readOnly
        onKeyDown={onKeyDown} tabIndex={state.phase === 'running' ? 0 : -1} />

      {state.phase === 'ended' && (
        <Card title="Game over — a word reached the target">
          <p className="text-sm">
            Score: <strong className="tabular-nums text-text-primary">{state.score}</strong>
            {record && <> · Best: <strong className="tabular-nums">{record.bestScore}</strong></>}
          </p>
          <div className="mt-4"><Button onClick={() => reset()}>Play again</Button></div>
        </Card>
      )}
    </div>
  );
}